/**
 * SyncModal — v0.8.6 (Bill 2026-05-15).
 *
 * Single unified "Sync" modal invoked from the IdentityShell header. On
 * open, fan out three reconciles in parallel:
 *   - Entra Reconcile          per-user Graph-attr deltas
 *   - Entra Global Suggest     per-role-source-group adds + archives
 *   - Core Director Reconcile  officer-code SuggestAssign / Stale / NameMismatch
 *
 * Results merge into one finding-per-row list with badges; user ticks
 * what to apply, clicks Apply, and the modal re-runs to show what's left.
 *
 * Replaces the SyncTab page entirely (deleted in this commit). No more
 * Source pills, no more Reconcile/Global Suggest mode distinction in UI
 * — all internal plumbing.
 *
 * Banno: not yet wired (post-2026-07-31 cutover); modal omits the section
 * until the api-client exposes the corresponding reconcile method.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, EyeOff } from '@retail-ops/panel-shell';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Pill, Spinner } from '@/components/primitives';
import { ModalShell } from '@/components/ModalShell';
import type {
  ReconcileResult,
  GlobalSuggestResult,
  EntraUserRow,
  CdSyncReconcileRow,
} from '@retail-ops/api-client';

type FindingKind =
  | 'entra-field'
  | 'entra-branch'   // v0.9.5 — branchCode broken out of entra-field so SysOps can filter org-chart moves separately from name/title/email churn (Bill 2026-05-15).
  | 'cd-suggest'
  | 'cd-stale'
  | 'cd-name-mismatch'
  | 'role-add'
  | 'role-archive';

interface Finding {
  key: string;
  kind: FindingKind;
  userId: number | null;          // null for entra-role-add of a brand-new user
  email: string;
  displayName: string;
  detail: string;                  // human-readable summary
  // backing data for apply:
  entraField?: { fieldName: string };
  cdAssign?: { officerCode: string; flagNameMismatch: boolean };
  cdStale?: { userId: number };
  cdNameAck?: { userId: number };
  roleAdd?: { roleCode: string; candidate: EntraUserRow };
  roleArchive?: { roleCode: string; userId: number };
  // Ignore action context — when set, a per-row "Ignore" button writes to
  // the appropriate UserFieldSyncIgnores / UserGroupSyncIgnores entry so
  // the finding won't surface in future syncs. CD findings have no ignore
  // mechanism today (no UserCdSyncIgnore table yet), so the field stays
  // undefined for those — UI renders a disabled / placeholder button.
  ignore?:
    | { kind: 'field'; userId: number; fieldName: string }
    | { kind: 'group'; entraObjectId: string; groupId: string };
}

export function SyncModal({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const client = useApiClient();
  const [computing, setComputing] = useState(false);
  const [computeErr, setComputeErr] = useState<string | null>(null);

  const [entra, setEntra] = useState<ReconcileResult | null>(null);
  const [global, setGlobal] = useState<GlobalSuggestResult | null>(null);
  const [cd, setCd] = useState<{ rows: ReadonlyArray<CdSyncReconcileRow>; operators: number; scanned: number } | null>(null);

  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [applyBanner, setApplyBanner] = useState<string | null>(null);
  // v0.8.8 — click-to-filter on the summary pills. Empty set = show all.
  // Non-empty = OR-filter (show findings whose kind is in the set).
  const [filterKinds, setFilterKinds] = useState<Set<FindingKind>>(new Set());
  // v0.8.8 — locally-removed findings after a successful Ignore. Avoids
  // re-running the three reconciles on every click; the row simply drops
  // off until the next full Re-compute.
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  // v0.9.3 — branch sync results (admin-level, not per-user findings).
  // Surfaced as a banner card at the top of the modal rather than as
  // rows in the user list.
  const [branchSync, setBranchSync] = useState<{
    cdRecordCount: number;
    inserted: number;
    renamed: number;
    cdNotInV3: number;
    entraMappedCount: number;
    entraMappings: ReadonlyArray<{ entraValue: string; userCount: number; matchedBranchName: string | null; applied: boolean }>;
  } | null>(null);

  const toggleFilter = (k: FindingKind) => {
    const next = new Set(filterKinds);
    next.has(k) ? next.delete(k) : next.add(k);
    setFilterKinds(next);
  };
  const clearFilters = () => setFilterKinds(new Set());

  const compute = useCallback(async () => {
    setComputing(true); setComputeErr(null); setApplyBanner(null);
    setEntra(null); setGlobal(null); setCd(null); setBranchSync(null); setAccepted(new Set());
    try {
      // v0.9.3 — fan out 4 reconciles in parallel: Entra Reconcile + Global
      // Suggest, CD operator-code Reconcile, AND CD branch sync. Branch
      // sync writes immediately (it's idempotent and admin-level — no
      // per-row accept needed); the others stage findings for Apply.
      const [eRes, gRes, cRes, bRes] = await Promise.allSettled([
        client.retail.admin.syncReconcile(),
        client.retail.admin.syncGlobalSuggest(),
        client.retail.admin.cdSyncReconcile(),
        client.retail.admin.branchesCdSync(),
      ]);
      const errors: string[] = [];
      if (eRes.status === 'fulfilled' && eRes.value.success) setEntra(eRes.value.data);
      else errors.push(`Entra Reconcile: ${eRes.status === 'rejected' ? eRes.reason : (eRes.value.data?.error ?? eRes.value.data?.code ?? 'failed')}`);
      if (gRes.status === 'fulfilled' && gRes.value.success) setGlobal(gRes.value.data);
      else errors.push(`Entra Global Suggest: ${gRes.status === 'rejected' ? gRes.reason : (gRes.value.data?.error ?? gRes.value.data?.code ?? 'failed')}`);
      if (cRes.status === 'fulfilled' && cRes.value.success && cRes.value.data) setCd({
        rows: cRes.value.data.rows ?? [],
        operators: cRes.value.data.cdOperatorCount ?? 0,
        scanned: cRes.value.data.scannedUserCount ?? 0,
      });
      else errors.push(`Core Director Reconcile: ${cRes.status === 'rejected' ? cRes.reason : ((cRes as PromiseFulfilledResult<{ success: boolean; error?: string; code?: string }>).value.error ?? 'failed')}`);
      if (bRes.status === 'fulfilled' && bRes.value.success && bRes.value.data) {
        setBranchSync({
          cdRecordCount: bRes.value.data.cdRecordCount,
          inserted: bRes.value.data.inserted,
          renamed: bRes.value.data.renamed,
          cdNotInV3: bRes.value.data.cdNotInV3,
          entraMappedCount: bRes.value.data.entraMappedCount,
          entraMappings: bRes.value.data.entraMappings,
        });
      } else if (bRes.status === 'rejected' || !bRes.value.success) {
        errors.push(`Branch sync: ${bRes.status === 'rejected' ? bRes.reason : (bRes.value.error ?? bRes.value.code ?? 'failed')}`);
      }
      if (errors.length > 0) setComputeErr(errors.join(' · '));
    } catch (e) {
      setComputeErr(e instanceof Error ? e.message : String(e));
    } finally {
      setComputing(false);
    }
  }, [client]);

  useEffect(() => {
    if (!open) return;
    void compute();
  }, [open, compute]);

  // Flatten the three result shapes into a single Finding[] list.
  // Includes hiddenKeys + filterKinds in deps so the rendered list reacts
  // to local ignores and pill-filters without re-fetching from the server.
  const findings = useMemo<ReadonlyArray<Finding>>(() => {
    const out: Finding[] = [];
    if (entra) {
      for (const u of entra.users) {
        for (const f of u.fields) {
          // v0.9.5 — branchCode gets its own kind so the summary pills can
          // separate "moved to a new branch" from "name/title/email
          // updates". Same apply path, same ignore path; just a different
          // bucket for human eyes (Bill 2026-05-15).
          const isBranch = f.fieldName === 'branchCode';
          out.push({
            key: `entra-field:${u.userId}:${f.fieldName}`,
            kind: isBranch ? 'entra-branch' : 'entra-field',
            userId: u.userId,
            email: u.email,
            displayName: u.displayName,
            detail: `${f.fieldName}: ${f.current ?? '(empty)'} → ${f.entra ?? '(empty)'}`,
            entraField: { fieldName: f.fieldName },
            ignore: { kind: 'field', userId: u.userId, fieldName: f.fieldName },
          });
        }
      }
    }
    if (global) {
      for (const sec of global.sections) {
        for (const c of sec.proposeAdds) {
          out.push({
            key: `role-add:${sec.roleCode}:${c.entraObjectId}`,
            kind: 'role-add',
            userId: null,
            email: c.mail ?? c.upn ?? '',
            displayName: c.displayName ?? c.mail ?? c.upn ?? '',
            detail: `Add role ${sec.roleCode} (${sec.roleName})`,
            roleAdd: { roleCode: sec.roleCode, candidate: c },
            ignore: { kind: 'group', entraObjectId: c.entraObjectId, groupId: sec.groupId },
          });
        }
        for (const a of sec.proposeArchives) {
          out.push({
            key: `role-archive:${sec.roleCode}:${a.userId}`,
            kind: 'role-archive',
            userId: a.userId,
            email: a.email,
            displayName: a.displayName,
            detail: `Revoke role ${sec.roleCode} (${sec.roleName}) — no longer in source group`,
            roleArchive: { roleCode: sec.roleCode, userId: a.userId },
            ignore: a.entraObjectId
              ? { kind: 'group', entraObjectId: a.entraObjectId, groupId: sec.groupId }
              : undefined,
          });
        }
      }
    }
    if (cd) {
      for (const r of cd.rows) {
        if (r.rowType === 'SuggestAssign' && r.proposedOfficerCode) {
          out.push({
            key: `cd-suggest:${r.userId}`,
            kind: 'cd-suggest',
            userId: r.userId,
            email: r.email,
            displayName: r.displayName,
            detail: `Assign operator initials ${r.proposedOfficerCode} (CD: ${r.cdDescriptor ?? '?'}, match ${r.matchScore}%)`,
            cdAssign: { officerCode: r.proposedOfficerCode, flagNameMismatch: r.matchScore < 100 },
          });
        } else if (r.rowType === 'Stale') {
          out.push({
            key: `cd-stale:${r.userId}`,
            kind: 'cd-stale',
            userId: r.userId,
            email: r.email,
            displayName: r.displayName,
            detail: `Operator initials ${r.currentOfficerCode ?? '?'} stale — clear + reflag for re-validation`,
            cdStale: { userId: r.userId },
          });
        } else if (r.rowType === 'NameMismatch') {
          out.push({
            key: `cd-name:${r.userId}`,
            kind: 'cd-name-mismatch',
            userId: r.userId,
            email: r.email,
            displayName: r.displayName,
            detail: `CD descriptor "${r.cdDescriptor ?? '?'}" ≠ ROP DisplayName (Entra wins; flag for SysOps)`,
            cdNameAck: { userId: r.userId },
          });
        }
      }
    }
    // Filter out locally-hidden (Ignored) rows + apply pill filter.
    return out.filter((f) => {
      if (hiddenKeys.has(f.key)) return false;
      if (filterKinds.size > 0 && !filterKinds.has(f.kind)) return false;
      return true;
    });
  }, [entra, global, cd, hiddenKeys, filterKinds]);

  // Unfiltered counts (used by the summary pills so they always show the
  // full picture, not the after-filter view).
  const unfilteredCounts = useMemo(() => {
    const c: Record<FindingKind, number> = {
      'entra-field': 0, 'entra-branch': 0, 'cd-suggest': 0, 'cd-stale': 0, 'cd-name-mismatch': 0,
      'role-add': 0, 'role-archive': 0,
    };
    const tally = (k: FindingKind) => { c[k] += 1; };
    if (entra) for (const u of entra.users) for (const f of u.fields) {
      if (hiddenKeys.has(`entra-field:${u.userId}:${f.fieldName}`)) continue;
      tally(f.fieldName === 'branchCode' ? 'entra-branch' : 'entra-field');
    }
    if (global) for (const sec of global.sections) {
      for (const cand of sec.proposeAdds) {
        if (!hiddenKeys.has(`role-add:${sec.roleCode}:${cand.entraObjectId}`)) tally('role-add');
      }
      for (const a of sec.proposeArchives) {
        if (!hiddenKeys.has(`role-archive:${sec.roleCode}:${a.userId}`)) tally('role-archive');
      }
    }
    if (cd) for (const r of cd.rows) {
      if (r.rowType === 'SuggestAssign' && !hiddenKeys.has(`cd-suggest:${r.userId}`)) tally('cd-suggest');
      else if (r.rowType === 'Stale' && !hiddenKeys.has(`cd-stale:${r.userId}`)) tally('cd-stale');
      else if (r.rowType === 'NameMismatch' && !hiddenKeys.has(`cd-name:${r.userId}`)) tally('cd-name-mismatch');
    }
    return c;
  }, [entra, global, cd, hiddenKeys]);

  // Group findings by user (display)
  const byUser = useMemo(() => {
    const map = new Map<string, Finding[]>();
    for (const f of findings) {
      const key = f.email || f.displayName || `__noemail__:${f.key}`;
      const arr = map.get(key) ?? [];
      arr.push(f);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [findings]);

  const toggle = (key: string) => {
    const next = new Set(accepted);
    next.has(key) ? next.delete(key) : next.add(key);
    setAccepted(next);
  };
  const toggleMany = (keys: ReadonlyArray<string>, select: boolean) => {
    const next = new Set(accepted);
    for (const k of keys) {
      if (select) next.add(k);
      else next.delete(k);
    }
    setAccepted(next);
  };
  const acceptAll = () => setAccepted(new Set(findings.map((f) => f.key)));
  const acceptNone = () => setAccepted(new Set());

  // Per-row Ignore: writes to UserFieldSyncIgnores or UserGroupSyncIgnores,
  // then hides the row LOCALLY (no full re-compute — per Bill 2026-05-15:
  // "clicking Ignore should not reprocess the sync query, that is just
  // annoying"). Next manual Re-compute or modal-reopen picks up the new
  // ignore-list state from the server. CD findings have no ignore backing
  // table yet — button stays disabled for those.
  const onIgnore = useCallback(async (f: Finding) => {
    if (!f.ignore) return;
    try {
      if (f.ignore.kind === 'field') {
        await client.retail.admin.createFieldSyncIgnore({
          userId: f.ignore.userId,
          fieldName: f.ignore.fieldName,
          reason: 'Ignored via Sync modal',
        });
      } else {
        await client.retail.admin.createGroupSyncIgnore({
          entraObjectId: f.ignore.entraObjectId,
          groupId: f.ignore.groupId,
          reason: 'Ignored via Sync modal',
        });
      }
      // Locally hide just this row.
      setHiddenKeys((prev) => {
        const next = new Set(prev);
        next.add(f.key);
        return next;
      });
      // Also drop it from accepted in case it was checked.
      setAccepted((prev) => {
        if (!prev.has(f.key)) return prev;
        const next = new Set(prev);
        next.delete(f.key);
        return next;
      });
    } catch (e) {
      setApplyBanner(`Ignore failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [client]);

  const apply = useCallback(async () => {
    if (accepted.size === 0) return;
    setApplying(true); setApplyBanner(null);
    const findingByKey = new Map(findings.map((f) => [f.key, f]));
    const selected = Array.from(accepted).map((k) => findingByKey.get(k)).filter((f): f is Finding => f != null);

    // Bucket selections by backend endpoint
    const entraByUser = new Map<number, string[]>();        // userId -> fieldNames
    const roleAdds = new Map<string, EntraUserRow[]>();     // roleCode -> candidates
    const roleArchives = new Map<string, number[]>();       // roleCode -> userIds
    const cdAssigns: { userId: number; officerCode: string; flagNameMismatch: boolean }[] = [];
    const cdStale: number[] = [];
    const cdNameAck: number[] = [];

    for (const f of selected) {
      if ((f.kind === 'entra-field' || f.kind === 'entra-branch') && f.userId !== null && f.entraField) {
        const list = entraByUser.get(f.userId) ?? [];
        list.push(f.entraField.fieldName);
        entraByUser.set(f.userId, list);
      } else if (f.kind === 'role-add' && f.roleAdd) {
        const list = roleAdds.get(f.roleAdd.roleCode) ?? [];
        list.push(f.roleAdd.candidate);
        roleAdds.set(f.roleAdd.roleCode, list);
      } else if (f.kind === 'role-archive' && f.roleArchive) {
        const list = roleArchives.get(f.roleArchive.roleCode) ?? [];
        list.push(f.roleArchive.userId);
        roleArchives.set(f.roleArchive.roleCode, list);
      } else if (f.kind === 'cd-suggest' && f.userId !== null && f.cdAssign) {
        cdAssigns.push({ userId: f.userId, officerCode: f.cdAssign.officerCode, flagNameMismatch: f.cdAssign.flagNameMismatch });
      } else if (f.kind === 'cd-stale' && f.cdStale) {
        cdStale.push(f.cdStale.userId);
      } else if (f.kind === 'cd-name-mismatch' && f.cdNameAck) {
        cdNameAck.push(f.cdNameAck.userId);
      }
    }

    const results: string[] = [];
    const errors: string[] = [];

    // Entra per-user apply (serial; backend already handles batching per call)
    for (const [userId, fields] of entraByUser.entries()) {
      try {
        const r = await client.retail.admin.syncSingleUserApply(userId, { acceptedFields: fields });
        if (r.success) results.push(`Entra user ${userId}: ${r.data.appliedFieldsCount} fields`);
        else errors.push(`Entra user ${userId}: ${r.error ?? r.code ?? 'failed'}`);
      } catch (e) { errors.push(`Entra user ${userId}: ${e instanceof Error ? e.message : String(e)}`); }
    }

    // Entra Global Suggest apply (one bundled call)
    if (roleAdds.size > 0 || roleArchives.size > 0) {
      try {
        const r = await client.retail.admin.syncGlobalSuggestApply({
          addsByRole: Array.from(roleAdds.entries()).map(([roleCode, candidates]) => ({ roleCode, candidates })),
          archivesByRole: Array.from(roleArchives.entries()).map(([roleCode, userIds]) => ({ roleCode, userIds })),
        });
        if (r.success) {
          results.push(`Roles: +${r.data.addedAssignmentCount} -${r.data.archivedAssignmentCount} (${r.data.addedUserCount} new users)`);
          if (r.data.errors.length > 0) errors.push(...r.data.errors);
        } else errors.push('Global Suggest apply failed');
      } catch (e) { errors.push(`Global Suggest: ${e instanceof Error ? e.message : String(e)}`); }
    }

    // CD apply (one bundled call)
    if (cdAssigns.length > 0 || cdStale.length > 0 || cdNameAck.length > 0) {
      try {
        const r = await client.retail.admin.cdSyncApply({
          assigns: cdAssigns,
          staleNullouts: cdStale,
          nameMismatchAcks: cdNameAck,
        });
        if (r.success && r.data) {
          results.push(`CD: assigned ${r.data.assignedCount}, stale ${r.data.staleNulledCount}, name-flags ${r.data.nameMismatchFlaggedCount}`);
          if (r.data.errors.length > 0) errors.push(...r.data.errors);
        } else errors.push('CD apply failed');
      } catch (e) { errors.push(`CD: ${e instanceof Error ? e.message : String(e)}`); }
    }

    setApplyBanner(
      `${results.join(' · ') || 'Nothing to apply.'}${errors.length > 0 ? ` · ${errors.length} error(s): ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '…' : ''}` : ''}`,
    );
    setApplying(false);
    // Re-run compute to refresh the list.
    await compute();
  }, [accepted, findings, client, compute]);


  return (
    <ModalShell open={open} title="Sync" onClose={onClose} width="min(1600px, 95vw)">
      <div className="sync-summary-row">
        <div>
          {findings.length} finding{findings.length === 1 ? '' : 's'} across {byUser.length} user{byUser.length === 1 ? '' : 's'}
          {filterKinds.size > 0 && (
            <> <span className="cell-dim">(filtered — </span>
              <button type="button" className="link-button" onClick={clearFilters}>clear</button>
              <span className="cell-dim">)</span>
            </>
          )}
          {' · '}
          <FilterPill kind="entra-field"      label="Entra fields"   count={unfilteredCounts['entra-field']}     tone="info"  active={filterKinds.has('entra-field')}     onClick={() => toggleFilter('entra-field')} />{' '}
          <FilterPill kind="entra-branch"     label="Branch"         count={unfilteredCounts['entra-branch']}    tone="info"  active={filterKinds.has('entra-branch')}    onClick={() => toggleFilter('entra-branch')} />{' '}
          <FilterPill kind="role-add"         label="Role add"       count={unfilteredCounts['role-add']}        tone="info"  active={filterKinds.has('role-add')}        onClick={() => toggleFilter('role-add')} />{' '}
          <FilterPill kind="role-archive"     label="Role revoke"    count={unfilteredCounts['role-archive']}    tone="warn"  active={filterKinds.has('role-archive')}    onClick={() => toggleFilter('role-archive')} />{' '}
          <FilterPill kind="cd-suggest"       label="CD suggest"     count={unfilteredCounts['cd-suggest']}      tone="ok"    active={filterKinds.has('cd-suggest')}      onClick={() => toggleFilter('cd-suggest')} />{' '}
          <FilterPill kind="cd-stale"         label="CD stale"       count={unfilteredCounts['cd-stale']}        tone="warn"  active={filterKinds.has('cd-stale')}        onClick={() => toggleFilter('cd-stale')} />{' '}
          <FilterPill kind="cd-name-mismatch" label="CD name flag"   count={unfilteredCounts['cd-name-mismatch']} tone="muted" active={filterKinds.has('cd-name-mismatch')} onClick={() => toggleFilter('cd-name-mismatch')} />
        </div>
        <Button variant="ghost" onClick={() => void compute()} disabled={computing}>
          <RefreshCw size={14} className={computing ? 'spin' : ''} /> {computing ? 'Computing…' : 'Re-compute'}
        </Button>
      </div>
      {computeErr && <Banner tone="err">{computeErr}</Banner>}
      {applyBanner && <Banner tone="info">{applyBanner}</Banner>}

      {/* v0.9.3 — branch sync ran in parallel; surface its results as a
          banner (not per-user findings). Idempotent — same banner on
          subsequent runs unless CD changes. */}
      {branchSync && (
        <Banner tone="info">
          Branches synced from CD: {branchSync.cdRecordCount} branches{' '}
          {branchSync.inserted > 0 && <>· {branchSync.inserted} inserted </>}
          {branchSync.renamed > 0 && <>· {branchSync.renamed} renamed </>}
          {branchSync.cdNotInV3 > 0 && <>· {branchSync.cdNotInV3} ROP-only </>}
          · Entra location mappings applied this run: {branchSync.entraMappedCount}.
          {branchSync.entraMappings.length > 0 && (
            <>
              {' '}<span className="cell-dim">Mappings:</span>{' '}
              {branchSync.entraMappings.map((m, i) => (
                <span key={m.entraValue}>
                  {i > 0 && ', '}
                  <code className="cell-mono">{m.entraValue}</code>
                  {' → '}
                  {m.matchedBranchName ? <strong>{m.matchedBranchName}</strong> : <span className="cell-dim">(no match)</span>}
                  {m.applied && <span className="cell-dim"> ✓</span>}
                </span>
              ))}
            </>
          )}
        </Banner>
      )}

      {computing && findings.length === 0 && <Spinner label="Computing sync findings…" />}

      {!computing && findings.length === 0 && (
        <p>Everything is in sync. No deltas across Entra, Core Director, or role-source groups.</p>
      )}

      {findings.length > 0 && (
        <>
          <div className="sync-user-list">
            {byUser.map(([userKey, userFindings]) => (
              <UserFindingsGroup
                key={userKey}
                findings={userFindings}
                accepted={accepted}
                onToggle={toggle}
                onToggleMany={toggleMany}
                onIgnore={onIgnore}
              />
            ))}
          </div>

          <div className="sync-apply-bar">
            <span>{accepted.size} of {findings.length} accepted.</span>
            <Button variant="ghost" size="sm" onClick={acceptAll} disabled={accepted.size === findings.length}>Accept all</Button>
            <Button variant="ghost" size="sm" onClick={acceptNone} disabled={accepted.size === 0}>Clear</Button>
            <Button variant="primary"
              disabled={applying || accepted.size === 0}
              onClick={() => void apply()}>
              {applying ? 'Applying…' : `Apply ${accepted.size}`}
            </Button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function UserFindingsGroup({
  findings, accepted, onToggle, onToggleMany, onIgnore,
}: {
  findings: ReadonlyArray<Finding>;
  accepted: Set<string>;
  onToggle: (key: string) => void;
  onToggleMany: (keys: ReadonlyArray<string>, select: boolean) => void;
  onIgnore: (f: Finding) => void | Promise<void>;
}): JSX.Element {
  const first = findings[0];
  const allKeys = findings.map((f) => f.key);
  const selectedCount = allKeys.filter((k) => accepted.has(k)).length;
  const allSelected = selectedCount === allKeys.length && allKeys.length > 0;
  const someSelected = selectedCount > 0 && !allSelected;
  const masterRef = useCallback((el: HTMLInputElement | null) => {
    if (el) el.indeterminate = someSelected;
  }, [someSelected]);

  return (
    <section className="sync-user-card">
      <header className="sync-user-card__header">
        <input
          type="checkbox"
          ref={masterRef}
          checked={allSelected}
          onChange={() => onToggleMany(allKeys, !allSelected)}
          aria-label={`Select all ${findings.length} findings for ${first.email}`}
        />
        <div className="sync-user-card__identity">
          <strong>{first.displayName || first.email || '(unknown)'}</strong>
          {first.email && <code className="cell-mono">{first.email}</code>}
        </div>
        <span className="sync-user-card__count cell-dim">
          {selectedCount} of {findings.length} selected
        </span>
      </header>
      <ul className="sync-user-card__findings">
        {findings.map((f) => (
          <li key={f.key}>
            <input
              type="checkbox"
              aria-label={`Accept ${f.detail} for ${f.email}`}
              checked={accepted.has(f.key)}
              onChange={() => onToggle(f.key)}
            />
            <SourceBadge kind={f.kind} />
            <span className="sync-user-card__detail">{f.detail}</span>
            <span title={f.ignore ? 'Stop surfacing this finding in future syncs.' : 'CD findings have no ignore mechanism yet.'}>
              <Button variant="ghost" size="sm"
                disabled={!f.ignore}
                onClick={() => void onIgnore(f)}>
                <EyeOff size={14} /> Ignore
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SourceBadge({ kind }: { kind: FindingKind }): JSX.Element {
  switch (kind) {
    case 'entra-field':       return <Pill tone="info">Entra · field</Pill>;
    case 'entra-branch':      return <Pill tone="info">Entra · branch</Pill>;
    case 'role-add':          return <Pill tone="info">Entra · role +</Pill>;
    case 'role-archive':      return <Pill tone="warn">Entra · role −</Pill>;
    case 'cd-suggest':        return <Pill tone="ok">CD · suggest</Pill>;
    case 'cd-stale':          return <Pill tone="warn">CD · stale</Pill>;
    case 'cd-name-mismatch':  return <Pill tone="muted">CD · name flag</Pill>;
  }
}

// v0.8.8 — interactive pill used in the summary row. Clicking toggles the
// kind in the parent's filterKinds set. Visual: full tone when active,
// outlined/muted when not. Count comes from the unfiltered tally so the
// pills always show "this many findings of this type exist" rather than
// flipping to zero when their own filter excludes the rest.
function FilterPill({
  kind: _kind, label, count, tone, active, onClick,
}: {
  kind: FindingKind;
  label: string;
  count: number;
  tone: 'ok' | 'warn' | 'info' | 'muted';
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`pill pill--${tone} pill--clickable${active ? ' pill--active' : ' pill--inactive'}`}
      onClick={onClick}
      title={`${active ? 'Hide' : 'Show only'} ${label} findings`}
    >
      {label} {count}
    </button>
  );
}
